import { FC, HTMLAttributes, KeyboardEvent, MouseEvent, useCallback } from 'react';
import ReactModal from 'react-modal';
import { Button, ButtonVariant } from '../Button';
import { Icon, IconName } from '../Graphics';
import { ColProps, Row } from '../Grid';
import { ModalSize } from '../../enums';
import { OneOrMoreReactElements } from '../../types';
import { joinClassNames } from '../../utils';
import { ModalSection } from './ModalSection';
import styles from './styles.module.scss';
import { useFullscreen } from '../Fullscreen';

export type ModalProps = HTMLAttributes<HTMLDivElement> & {
  children: OneOrMoreReactElements<ColProps>;
  isOpen: boolean;
  onRequestClose: (event: MouseEvent | KeyboardEvent) => void;
  hideClose?: boolean;
  rootElement?: string;
  size?: ModalSize;
  reactModalProps?: Omit<ReactModal.Props, 'isOpen'>;
};

type ModalSubcomponents = {
  Section: typeof ModalSection;
};

export const Modal: FC<ModalProps> & ModalSubcomponents = ({
  children,
  isOpen,
  onRequestClose,
  hideClose = false,
  rootElement = '#root',
  size = ModalSize.Medium,
  className,
  reactModalProps,
}) => {
  const fullscreen = useFullscreen();
  ReactModal.setAppElement(rootElement);

  const parentSelector = useCallback(
    () => (fullscreen.container instanceof HTMLElement ? fullscreen.container : document.body),
    [fullscreen.container]
  );

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      className={joinClassNames(styles.modal, styles[`size--${size}` as keyof typeof styles], className)}
      overlayClassName={styles.overlay}
      parentSelector={parentSelector}
      {...reactModalProps}
    >
      {!hideClose && (
        <div className={styles.close}>
          <Button onClick={onRequestClose} variant={ButtonVariant.Transparent}>
            Close
            <Icon name={IconName.Close} marginLeft="small" size=".85em" />
          </Button>
        </div>
      )}
      <Row>{children}</Row>
    </ReactModal>
  );
};

Modal.Section = ModalSection;
